/**
 * Field validation manager
 */
class FieldValidator {
    constructor() {
        this.MAX_FILE_SIZE_BYTES = 30 * 1024 * 1024; // 30MB
        this.ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];
    }

    validateField(field) {
        const value = (field.value || '').trim();

        if (field.id === 'purchase-date') {
            return this.validateDateField(field);
        }

        if (field.type === 'file') {
            return this.validateFileField(field);
        }

        return this.validateGenericField(field);
    }

    parsePurchaseDate(value) {
        const dateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
        if (!value || value.length !== 10 || !dateRegex.test(value)) {
            return null;
        }

        const [, month, day, year] = value.match(dateRegex);
        const monthNum = parseInt(month, 10);
        const dayNum = parseInt(day, 10);
        const yearNum = parseInt(year, 10);

        if (monthNum < 1 || monthNum > 12) return null;

        const daysInMonth = DateUtils.getDaysInMonth(monthNum, yearNum);
        if (dayNum < 1 || dayNum > daysInMonth) return null;

        return new Date(yearNum, monthNum - 1, dayNum);
    }

    validateDateField(field) {
        const value = field.value.trim();
        const dateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;

        if (!value && field.hasAttribute('required')) {
            return 'Purchase date is required.';
        }

        if (!dateRegex.test(value)) {
            return 'Please enter date in MM/DD/YYYY format.';
        }

        if (value && value.length === 10) {
            const [, month, day, year] = value.match(dateRegex);
            const monthNum = parseInt(month, 10);
            const dayNum = parseInt(day, 10);
            const yearNum = parseInt(year, 10);
            const currentYear = new Date().getFullYear();

            if (monthNum < 1 || monthNum > 12) {
                return 'Month must be between 01 and 12.';
            }

            if (yearNum < 1900) {
                return 'Please enter a valid purchase year.';
            }

            if (yearNum > currentYear + 1) {
                return 'This date is invalid. Please enter a purchase date that is not in the future.';
            }

            const daysInMonth = DateUtils.getDaysInMonth(monthNum, yearNum);
            if (dayNum < 1 || dayNum > daysInMonth) {
                return `Day must be between 01 and ${daysInMonth} for ${DateUtils.getMonthName(monthNum)} ${yearNum}.`;
            }

            const inputDate = new Date(yearNum, monthNum - 1, dayNum);
            const today = new Date();
            today.setHours(23, 59, 59, 999);

            if (inputDate > today) {
                return 'This date is invalid. Purchase date cannot be in the future.';
            }
        }

        return null;
    }

    getDateWarning(field) {
        if (this.validateDateField(field)) return null;

        const inputDate = this.parsePurchaseDate(field.value.trim());
        if (!inputDate) return null;

        const twoYearsAgo = new Date();
        twoYearsAgo.setHours(0, 0, 0, 0);
        twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);

        if (inputDate < twoYearsAgo) {
            return 'Please double-check your purchase date. This date is more than two years in the past.';
        }

        return null;
    }

    applyDateFieldFeedback(field) {
        const error = this.validateDateField(field);
        if (error) {
            this.showFieldError(field, error);
            return error;
        }

        this.clearFieldError(field);
        const warning = this.getDateWarning(field);
        if (warning) {
            this.showFieldWarning(field, warning);
        } else {
            this.clearFieldWarning(field);
        }

        return null;
    }

    validateFileField(field) {
        const files = field.files;
        if (!files || files.length === 0) {
            return field.hasAttribute('required') ? 'Please upload at least one file.' : null;
        }

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            if (!this.ALLOWED_MIME_TYPES.includes(file.type)) {
                return 'Only JPG, JPEG, PNG or PDF files are allowed.';
            }
            if (file.size > this.MAX_FILE_SIZE_BYTES) {
                return 'File size must be 30 MB or less.';
            }
        }
        return null;
    }

    validateGenericField(field) {
        const value = (field.value || '').trim();

        if (field.hasAttribute('required') && value === '') {
            return 'This field is required.';
        }

        if (typeof field.checkValidity === 'function' && field.validity) {
            const validity = field.validity;

            if (validity.tooShort && field.getAttribute('minlength')) {
                return `Please enter at least ${field.getAttribute('minlength')} characters.`;
            }
            if (validity.tooLong && field.getAttribute('maxlength')) {
                return `Please enter no more than ${field.getAttribute('maxlength')} characters.`;
            }
            if (validity.typeMismatch) {
                if (field.type === 'email') return 'Please enter a valid email address.';
                if (field.type === 'url') return 'Please enter a valid URL.';
            }
            if (validity.patternMismatch) {
                return 'Please match the requested format.';
            }
            if (validity.rangeUnderflow && field.getAttribute('min') !== null) {
                return `Please enter a value greater than or equal to ${field.getAttribute('min')}.`;
            }
            if (validity.rangeOverflow && field.getAttribute('max') !== null) {
                return `Please enter a value less than or equal to ${field.getAttribute('max')}.`;
            }
            if (validity.stepMismatch) {
                return 'Please enter a valid value.';
            }
            if (validity.badInput && field.type === 'number') {
                return 'Please enter a number.';
            }
        }

        return null;
    }

    showFieldError(field, message) {
        const formGroup = field.closest('.form-group') || field.parentElement;
        if (!formGroup) return;

        this.clearFieldError(field);
        this.clearFieldWarning(field);
        field.classList.add('field-error');
        field.setAttribute('aria-invalid', 'true');

        const errorEl = document.createElement('div');
        errorEl.className = 'error-message';
        errorEl.textContent = message || 'This field is required.';

        if (field.id) {
            errorEl.id = field.id + '-error';
            field.setAttribute('aria-describedby', errorEl.id);
        }

        formGroup.appendChild(errorEl);
    }

    clearFieldError(field) {
        const formGroup = field.closest('.form-group') || field.parentElement;
        if (!formGroup) return;

        field.classList.remove('field-error');
        field.removeAttribute('aria-invalid');

        const existing = formGroup.querySelector('.error-message');
        if (existing) existing.remove();

        if (field.id && !formGroup.querySelector('.warning-message')) {
            field.removeAttribute('aria-describedby');
        }
    }

    showFieldWarning(field, message) {
        const formGroup = field.closest('.form-group') || field.parentElement;
        if (!formGroup) return;

        this.clearFieldError(field);
        this.clearFieldWarning(field);
        field.classList.add('field-warning');

        const warningEl = document.createElement('div');
        warningEl.className = 'warning-message';
        warningEl.textContent = message;

        if (field.id) {
            warningEl.id = field.id + '-warning';
            field.setAttribute('aria-describedby', warningEl.id);
        }

        formGroup.appendChild(warningEl);
    }

    clearFieldWarning(field) {
        const formGroup = field.closest('.form-group') || field.parentElement;
        if (!formGroup) return;

        field.classList.remove('field-warning');

        const existing = formGroup.querySelector('.warning-message');
        if (existing) existing.remove();

        if (field.id && !formGroup.querySelector('.error-message')) {
            field.removeAttribute('aria-describedby');
        }
    }
}

